import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AuthLoadingScreen from './AuthLoadingScreen';

/**
 * GlobalAuthGate - Single top-level auth gate for the entire app
 * 
 * This is the ONLY place that renders AuthLoadingScreen.
 * Route guards should NOT show their own loading screens anymore.
 * 
 * CRITICAL RULES:
 * 1. NEVER render null or empty fragment - always show loader OR children
 * 2. Minimum display time prevents micro-flicker during fast auth checks
 * 3. Uses fixed positioning to cover any potential blank frames
 */

const MIN_LOADER_DISPLAY_MS = 400;

// Custom hook to manage minimum display time
function useMinDisplayTime(isLoading) {
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);
  const timerRef = useRef(null);
  const prevIsLoadingRef = useRef(isLoading);

  // Stable callback for timer
  const onTimerComplete = useCallback(() => {
    setMinTimeElapsed(true);
  }, []);

  useEffect(() => {
    // Cleanup existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    const wasLoading = prevIsLoadingRef.current;
    const nowLoading = isLoading;
    prevIsLoadingRef.current = isLoading;

    if (nowLoading && !wasLoading) {
      // Transitioning TO loading - reset elapsed flag
      // This is intentional - we need to reset state when loading starts again
      setMinTimeElapsed(false);
    } else if (!nowLoading && wasLoading) {
      // Transitioning FROM loading - start minimum time countdown
      timerRef.current = setTimeout(onTimerComplete, MIN_LOADER_DISPLAY_MS);
    } else if (!nowLoading && !wasLoading && !minTimeElapsed) {
      // Already not loading but timer hasn't fired - ensure it does
      timerRef.current = setTimeout(onTimerComplete, MIN_LOADER_DISPLAY_MS);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [isLoading, minTimeElapsed, onTimerComplete]);

  // Show loading if actively loading OR minimum time hasn't elapsed
  return isLoading || !minTimeElapsed;
}

const GlobalAuthGate = ({ children }) => {
  const { authStatus } = useAuth();
  
  const isChecking = authStatus === 'checking';
  const shouldShowLoader = useMinDisplayTime(isChecking);

  // Dev logging in effect
  useEffect(() => {
    // Log ALL auth status changes, not just in dev
    console.log('[GLOBAL AUTH GATE]', { authStatus, isChecking, shouldShowLoader, timestamp: new Date().toISOString() });
    
    // CRITICAL: Warn if authStatus changes to 'checking' after initial load
    if (authStatus === 'checking') {
      console.warn('[GLOBAL AUTH GATE] ⚠️ Auth status changed to CHECKING - this will unmount children!');
      console.trace('[GLOBAL AUTH GATE] Auth checking stack trace');
    }
  }, [authStatus, shouldShowLoader, isChecking]);

  // RULE: Never render empty - always show loader OR children
  if (shouldShowLoader) {
    return <AuthLoadingScreen />;
  }

  // Auth check complete and minimum display time elapsed
  return (
    <div className="min-h-screen bg-[#161B26]">
      {children}
    </div>
  );
};

export default GlobalAuthGate;
